import classNames from 'classnames';

import { Button, Modal } from 'components-antd';
import { Icons } from './../../Icons';
import { Checkmark, Delete, Dots, Edit } from 'components/Icons';
import placeholderImg from './image.png';
import { Options, TruncatedContent } from 'components';
import { useState } from 'react';
import { getLink } from 'helpers';
import { accessOptions } from '../LinkModal';
import { useSelector } from 'react-redux';
import { getTransactionAccessSelector } from 'store/selectors/transaction';
import { getCurrentLoggedInUserId } from 'store/selectors/user';

import modalStyles from './../LinkModal/styles.module.scss';
import styles from './styles.module.scss';

export enum LINK_CARD_VARIANTS {
  GREEN = '#04a451',
  BLUE = '#4673d1',
  DARK_BLUE = '#006AFF',
  DARK_BLUE_2 = '#0061FE',
  RED = '#ff576d',
  RED_2 = '#DE3341',
  RED_3 = '#D92228',
  PURPLE = '#928CDA',
  CONFETTI_BLUE = '#51BFE1',
  ORANGE = '#FB913A',
  ORANGE_2 = '#FF850F',
  GRAY = '#252D44',
}

export const LinkCard = ({
  name,
  desc,
  link,
  createdBy,
  isImage = false,
  isGooglePDF = false,
  isGoogleSlides = false,
  isGoogleSheet = false,
  isGoogleDoc = false,
  isZillow = false,
  isDropbox = false,
  isRedfin = false,
  isHomes = false,
  isRealtor = false,
  isUnknown = false,
  access,
  variant = '',
  onEdit,
  onDelete,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const bgCardColor = variant ? `${variant}1f` : 'white';
  const [showDescriptionView, setShowDescriptionView] = useState(false);
  const { fullAccess } = useSelector(getTransactionAccessSelector);
  const loggedInUserId = useSelector(getCurrentLoggedInUserId);
  const isAllowedToModifyLink = fullAccess || createdBy === loggedInUserId;

  const getIconVariant = () => {
    if (isGooglePDF || isGoogleSlides) return Icons.PDF_LINK;
    else if (isGoogleSheet) return Icons.GOOGLE_SHEET_LINK;
    else if (isGoogleDoc) return Icons.DOC_LINK;
    else if (isZillow) return Icons.ZILLOW;
    else if (isRedfin) return Icons.REDFIN;
    else if (isHomes) return Icons.HOMES;
    else if (isDropbox) return Icons.DROPBOX;
    else if (isRealtor) return Icons.REALTOR;
    else if (isUnknown) return Icons.UNKNOWN_LINK;
  };

  const getBgCardColor = () => {
    if (isGooglePDF) return `${LINK_CARD_VARIANTS.ORANGE}1f`;
    else if (isGoogleSlides) return `${LINK_CARD_VARIANTS.RED}1f`;
    else if (isGoogleSheet) return `${LINK_CARD_VARIANTS.GREEN}1f`;
    else if (isGoogleDoc) return `${LINK_CARD_VARIANTS.BLUE}1f`;
    else if (isZillow) return LINK_CARD_VARIANTS.DARK_BLUE;
    else if (isHomes) return LINK_CARD_VARIANTS.ORANGE_2;
    else if (isRedfin) return LINK_CARD_VARIANTS.RED_2;
    else if (isDropbox) return LINK_CARD_VARIANTS.DARK_BLUE_2;
    else if (isRealtor) return LINK_CARD_VARIANTS.RED_3;
    else if (isUnknown) return bgCardColor;
  };

  const onClickCard = () => window.open(getLink(link), '_blank');

  const OPTIONS = [
    {
      label: 'Edit',
      icon: <Edit stroke="#FF576D" />,
      onClick: onEdit,
    },
    {
      label: 'Delete',
      icon: <Delete color="#FF576D" />,
      onClick: onDelete,
    },
  ];

  const handleCopy = (e) => {
    e.preventDefault();
    e.stopPropagation();

    navigator.clipboard.writeText(link);
    setShowTooltip(true);
    setTimeout(() => setShowTooltip(false), 1000);
  };

  const getAccessIcon = () => {
    const accessTypeFound = accessOptions.find((item) => item?.label === access);
    if (accessTypeFound) return accessTypeFound?.icon;

    return null;
  };

  return (
    <div className={styles.linkCard}>
      <div className={styles.cardTop} style={{ backgroundColor: getBgCardColor() }}>
        {isImage ? (
          <img src={placeholderImg} alt="" />
        ) : (
          <Icons variant={getIconVariant()} color={isUnknown ? variant : ''} />
        )}
      </div>
      <div className={styles.cardBottom}>
        <p className={styles.name} onClick={onClickCard}>
          {name}
        </p>
        <TruncatedContent
          content={desc}
          showMoreText="Read More"
          showMoreOnClick={() => setShowDescriptionView(true)}
          maxLines={2}
          className={styles.description}
        />
        <div className={styles.buttonsContainer}>
          <div className={styles.left}>
            <div className={classNames(styles.roundedBorder, styles.roundedBorderIcon)}>
              {getAccessIcon()}
            </div>
            <Button
              variant="hollow-bordered"
              className={classNames(styles.copyBtn, { [styles.copiedText]: showTooltip })}
              onClick={handleCopy}
              disabled={showTooltip}
            >
              {!showTooltip ? 'Copy Link' : 'Copied'}
            </Button>
          </div>
          <div className={styles.right}>
            {isAllowedToModifyLink && (
              <div className={styles.roundedBorder}>
                <Options
                  title={<Dots className={styles.icon} />}
                  placement="bottom"
                  options={OPTIONS}
                  popoverClassName={styles.optionsPopover}
                  titleClassName={styles.optionsTitle}
                  getPopupContainer={(trigger) => trigger}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {showDescriptionView && (
        <Modal
          open={showDescriptionView}
          onCancel={() => setShowDescriptionView(false)}
          className={modalStyles.trxLinkModal}
          footer={null}
          width={675}
        >
          <p className={modalStyles.header}>{name}</p>
          <p className={styles.descriptionText}>{desc}</p>
          <div className={styles.modalFooter}>
            <div
              onClick={handleCopy}
              className={classNames(styles.copyBtn, { [styles.copiedText]: showTooltip })}
            >
              {!showTooltip ? 'Copy Link' : 'Copied'}
              {showTooltip && <Checkmark color="#04a451" />}
            </div>
            <Button className={styles.btn} variant="secondary" onClick={onClickCard}>
              Open Link
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
};
